import { useCallback, useMemo, useState } from 'react';
import type { AIGeneratedContent, PricingBreakdown } from '@data/publicPlans';
import { formatPlanInstruction } from '@utils/planInstructionFormat';
import {
	defaultPricingBreakdown,
	getMaterialsPlusLaborTotal,
	normalizePricingBreakdown
} from '@utils/pricingBreakdownUtils';
import PlanIndicativePricingCard from '@components/plan/PlanIndicativePricingCard';

type CopyFn = (en: string, de: string, hr: string) => string;

export type PlanEditorSection = 'description' | 'features' | 'instructions' | 'pricing';

const ALL_SECTIONS: PlanEditorSection[] = ['description', 'features', 'instructions', 'pricing'];

export type PlanTextEditorProps = {
	content: AIGeneratedContent;
	onContentChange: (next: AIGeneratedContent) => void;
	copy: CopyFn;
	/** Which cards to render */
	sections?: PlanEditorSection[];
	/** Show edit toggle and allow inline editing */
	editable?: boolean;
	/** Controlled edit mode (parent toolbar) */
	isEditing?: boolean;
	/** Hide built-in Edit/Save toolbar when parent provides PlanEditToolbar */
	showEditControls?: boolean;
	/** Show save button when editing (caller handles persist) */
	onSave?: () => void | Promise<void>;
	isSaving?: boolean;
	className?: string;
};

/**
 * Editable plan text sections for company workflows.
 */
export default function PlanTextEditor({
	content,
	onContentChange,
	copy,
	sections = ALL_SECTIONS,
	editable = false,
	isEditing: isEditingProp,
	showEditControls = true,
	onSave,
	isSaving = false,
	className = ''
}: PlanTextEditorProps) {
	const [internalEditing, setInternalEditing] = useState(false);
	const isControlled = isEditingProp !== undefined;
	const isEditing = isControlled ? isEditingProp : internalEditing;
	const showToolbar = editable && showEditControls && !isControlled;

	const show = (section: PlanEditorSection) => sections.includes(section);

	const pricing = content.pricingBreakdown ?? defaultPricingBreakdown();
	const materialsPlusLabor = useMemo(() => getMaterialsPlusLaborTotal(pricing), [pricing]);

	const update = useCallback(
		(patch: Partial<AIGeneratedContent>) => {
			onContentChange({ ...content, ...patch });
		},
		[content, onContentChange]
	);

	const updatePricing = useCallback(
		(mutate: (pb: PricingBreakdown) => PricingBreakdown, midOverride?: number) => {
			const next = normalizePricingBreakdown(mutate(content.pricingBreakdown ?? defaultPricingBreakdown()), midOverride);
			update({ pricingBreakdown: next });
		},
		[content.pricingBreakdown, update]
	);

	const applyPricingFactor = useCallback(
		(factor: number) => {
			updatePricing((pb) => {
				const materials = (Array.isArray(pb.materials) ? pb.materials : []).map((item) => {
					const unitPrice = Math.max(0, Math.round(Number(item.unitPrice || 0) * factor));
					return {
						...item,
						unitPrice,
						total: Number(item.quantity || 0) * unitPrice
					};
				});
				const labor = (Array.isArray(pb.labor) ? pb.labor : []).map((item) => {
					const ratePerHour = Math.max(0, Math.round(Number(item.ratePerHour || 0) * factor));
					return {
						...item,
						ratePerHour,
						total: Number(item.hours || 0) * ratePerHour
					};
				});
				return {
					...pb,
					materials,
					labor
				};
			});
		},
		[updatePricing]
	);

	const features = Array.isArray(content.keyFeatures) ? content.keyFeatures : [];
	const instructions = Array.isArray(content.instructions) ? content.instructions : [];
	const materials = Array.isArray(pricing.materials) ? pricing.materials : [];
	const labor = Array.isArray(pricing.labor) ? pricing.labor : [];

	return (
		<div className={className}>
			{showToolbar && (
				<div className="d-flex justify-content-end align-items-center gap-2 mb-3">
					{isEditing && onSave && (
						<button
							type="button"
							className="btn btn-outline-success btn-sm"
							disabled={isSaving}
							onClick={() => void onSave()}
						>
							{isSaving ? (
								<>
									<span className="spinner-border spinner-border-sm me-1" aria-hidden />
									{copy('Saving…', 'Speichern…', 'Spremanje…')}
								</>
							) : (
								<>
									<i className="bi bi-save me-1" aria-hidden />
									{copy('Save changes', 'Änderungen speichern', 'Spremi promjene')}
								</>
							)}
						</button>
					)}
					<button
						type="button"
						className="btn btn-success px-4 py-2 rounded-2 d-inline-flex align-items-center gap-2 shadow-sm"
						onClick={() => setInternalEditing((prev) => !prev)}
					>
						{isEditing ? (
							<>
								<i className="bi bi-check-lg" aria-hidden />
								<span>{copy('Done editing', 'Bearbeitung beendet', 'Završeno uređivanje')}</span>
							</>
						) : (
							<>
								<i className="bi bi-pencil-square" aria-hidden />
								<span>{copy('Edit plan', 'Plan bearbeiten', 'Uredi plan')}</span>
							</>
						)}
					</button>
				</div>
			)}

			{show('description') && (
				<div className="card mb-4">
					<div className="card-header">
						<h5 className="mb-0">{copy('Description', 'Beschreibung', 'Opis')}</h5>
					</div>
					<div className="card-body">
						{isEditing && editable ? (
							<textarea
								className="form-control"
								rows={4}
								value={content.planDescription ?? ''}
								onChange={(e) => update({ planDescription: e.target.value })}
							/>
						) : (
							<div className="text-body" style={{ whiteSpace: 'pre-wrap' }}>
								{content.planDescription || copy('No description yet.', 'Noch keine Beschreibung.', 'Još nema opisa.')}
							</div>
						)}
					</div>
				</div>
			)}

			{show('features') && (
				<div className="card mb-4">
					<div className="card-header d-flex justify-content-between align-items-center">
						<h5 className="mb-0">{copy('Key Features', 'Hauptmerkmale', 'Ključne značajke')}</h5>
						{isEditing && editable && (
							<button
								type="button"
								className="btn btn-sm btn-outline-success"
								onClick={() => update({ keyFeatures: [...features, ''] })}
							>
								+ {copy('Add', 'Hinzufügen', 'Dodaj')}
							</button>
						)}
					</div>
					<div className="card-body">
						{isEditing && editable ? (
							<ul className="list-unstyled mb-0">
								{features.map((feature, idx) => (
									<li key={idx} className="mb-3 d-flex align-items-start gap-2">
										<input
											type="text"
											className="form-control form-control-sm flex-grow-1"
											value={feature}
											onChange={(e) => {
												const next = [...features];
												next[idx] = e.target.value;
												update({ keyFeatures: next });
											}}
										/>
										<button
											type="button"
											className="btn btn-sm btn-outline-danger p-1"
											title={copy('Remove', 'Entfernen', 'Ukloni')}
											onClick={() => update({ keyFeatures: features.filter((_, i) => i !== idx) })}
										>
											<i className="bi bi-trash text-danger" aria-hidden />
										</button>
									</li>
								))}
							</ul>
						) : (
							<ul className="list-unstyled mb-0">
								{features.map((feature, idx) => (
									<li key={idx} className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-check2 text-success mt-1" aria-hidden />
										<span>{feature}</span>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			)}

			{show('instructions') && (
				<div className="card mb-4">
					<div className="card-header d-flex justify-content-between align-items-center">
						<h5 className="mb-0">{copy('Implementation Instructions', 'Umsetzungsschritte', 'Upute za izvedbu')}</h5>
						{isEditing && editable && (
							<button
								type="button"
								className="btn btn-sm btn-outline-success"
								onClick={() => update({ instructions: [...instructions, ''] })}
							>
								+ {copy('Add', 'Hinzufügen', 'Dodaj')}
							</button>
						)}
					</div>
					<div className="card-body">
						{isEditing && editable ? (
							<ol className="mb-0">
								{instructions.map((instruction, idx) => (
									<li key={idx} className="mb-3 d-flex align-items-start gap-2" style={{ lineHeight: '1.8' }}>
										<span className="me-1">{idx + 1}.</span>
										<input
											type="text"
											className="form-control form-control-sm flex-grow-1"
											value={instruction}
											onChange={(e) => {
												const next = [...instructions];
												next[idx] = e.target.value;
												update({ instructions: next });
											}}
										/>
										<button
											type="button"
											className="btn btn-sm btn-outline-danger p-1"
											title={copy('Remove', 'Entfernen', 'Ukloni')}
											onClick={() => update({ instructions: instructions.filter((_, i) => i !== idx) })}
										>
											<i className="bi bi-trash text-danger" aria-hidden />
										</button>
									</li>
								))}
							</ol>
						) : (
							<ol className="mb-0 ps-3" style={{ lineHeight: '1.8' }}>
								{instructions.map((instruction, idx) => (
									<li key={idx} className="mb-2">
										{formatPlanInstruction(instruction)}
									</li>
								))}
							</ol>
						)}
					</div>
				</div>
			)}

			{show('pricing') && (
				<div className="card mb-4">
					<div className="card-header">
						<h5 className="mb-0">
							<i className="bi bi-currency-euro text-success me-2" aria-hidden />
							{copy('Estimated Pricing', 'Geschätzte Kosten', 'Procijenjena cijena')}
						</h5>
					</div>
					<div className="card-body">
						{isEditing && editable ? (
							<>
								<div className="mb-3">
									<label className="form-label small text-muted mb-1">
										{copy('Estimated total (€)', 'Geschätzter Gesamtpreis (€)', 'Procijenjeni ukupni iznos (€)')}
									</label>
									<div className="d-flex flex-wrap gap-2 align-items-center">
										<input
											type="number"
											className="form-control fw-semibold"
											style={{ maxWidth: '12rem' }}
											value={pricing.mid || ''}
											onChange={(e) => {
												const v = Number(e.target.value);
												if (!Number.isNaN(v)) updatePricing((pb) => pb, v);
											}}
										/>
										<button
											type="button"
											className="btn btn-sm btn-outline-secondary"
											onClick={() => updatePricing((pb) => pb, materialsPlusLabor)}
										>
											{copy('Set to cost', 'Auf Kosten setzen', 'Postavi na trošak')}
										</button>
									</div>
									<div className="small text-muted mt-1">
										{copy('Minimum (materials + labor):', 'Minimum (Material + Arbeit):', 'Minimum (materijali + rad):')}{' '}
										{materialsPlusLabor.toLocaleString()} EUR
									</div>
								</div>
							<div className="mb-3 p-2 border rounded bg-light small">
								<div className="fw-medium mb-1">{copy('Adjust all pricing', 'Alle Preise anpassen', 'Prilagodi sve cijene')}</div>
								<div className="d-flex flex-wrap align-items-center gap-2">
									{[5, 10, -5].map((pct) => (
										<button
											key={pct}
											type="button"
											className="btn btn-sm btn-outline-secondary"
											onClick={() => applyPricingFactor(1 + pct / 100)}
										>
											{pct > 0 ? `+${pct}%` : `${pct}%`}
										</button>
									))}
									<span className="text-muted">
										{copy(
											'Applies to estimate, materials, and labor.',
											'Wird auf Schätzung, Materialien und Arbeit angewendet.',
											'Primjenjuje se na procjenu, materijale i rad.'
										)}
									</span>
								</div>
							</div>
								<hr />
								<div className="small mb-3">
									<div className="d-flex justify-content-between align-items-center mb-2">
										<span className="text-muted fw-semibold">{copy('Materials', 'Materialien', 'Materijali')}</span>
										<button
											type="button"
											className="btn btn-sm btn-outline-success py-0"
											onClick={() =>
												updatePricing((pb) => ({
													...pb,
													materials: [...pb.materials, { name: '', quantity: 0, unit: 'pcs', unitPrice: 0, total: 0 }]
												}))
											}
										>
											+ {copy('Add', 'Hinzufügen', 'Dodaj')}
										</button>
									</div>
									{materials.length === 0 ? (
										<p className="text-muted mb-0">{copy('No materials listed.', 'Keine Materialien.', 'Nema materijala.')}</p>
									) : (
										materials.map((m, idx) => (
											<div key={idx} className="mb-2 p-2 border rounded">
												<div className="d-flex gap-1 mb-1">
													<input
														type="text"
														className="form-control form-control-sm flex-grow-1"
														placeholder={copy('Material', 'Material', 'Materijal')}
														value={m.name}
														onChange={(e) =>
															updatePricing((pb) => {
																const next = [...pb.materials];
																const row = { ...next[idx], name: e.target.value };
																row.total = row.quantity * row.unitPrice;
																next[idx] = row;
																return { ...pb, materials: next };
															})
														}
													/>
													<button
														type="button"
														className="btn btn-sm btn-outline-danger p-1"
														onClick={() =>
															updatePricing((pb) => ({
																...pb,
																materials: pb.materials.filter((_, i) => i !== idx)
															}))
														}
													>
														<i className="bi bi-trash text-danger" aria-hidden />
													</button>
												</div>
												<div className="d-flex flex-wrap gap-1 align-items-center">
													<input
														type="number"
														className="form-control form-control-sm"
														style={{ width: '4.5rem' }}
														placeholder={copy('Qty', 'Menge', 'Kol.')}
														value={m.quantity || ''}
														onChange={(e) =>
															updatePricing((pb) => {
																const next = [...pb.materials];
																const q = Number(e.target.value) || 0;
																next[idx] = { ...next[idx], quantity: q, total: q * next[idx].unitPrice };
																return { ...pb, materials: next };
															})
														}
													/>
													<input
														type="text"
														className="form-control form-control-sm"
														style={{ width: '3.5rem' }}
														placeholder={copy('Unit', 'Einh.', 'Jed.')}
														value={m.unit}
														onChange={(e) =>
															updatePricing((pb) => {
																const next = [...pb.materials];
																next[idx] = { ...next[idx], unit: e.target.value };
																return { ...pb, materials: next };
															})
														}
													/>
													<input
														type="number"
														className="form-control form-control-sm"
														style={{ width: '5rem' }}
														placeholder={copy('Price', 'Preis', 'Cijena')}
														value={m.unitPrice || ''}
														onChange={(e) =>
															updatePricing((pb) => {
																const next = [...pb.materials];
																const p = Number(e.target.value) || 0;
																next[idx] = { ...next[idx], unitPrice: p, total: next[idx].quantity * p };
																return { ...pb, materials: next };
															})
														}
													/>
													<span className="small">{Number(m.total || 0).toLocaleString()} EUR</span>
												</div>
											</div>
										))
									)}
								</div>
								<hr />
								<div className="small">
									<div className="d-flex justify-content-between align-items-center mb-2">
										<span className="text-muted fw-semibold">{copy('Labor', 'Arbeit', 'Rad')}</span>
										<button
											type="button"
											className="btn btn-sm btn-outline-success py-0"
											onClick={() =>
												updatePricing((pb) => ({
													...pb,
													labor: [...pb.labor, { name: '', hours: 0, ratePerHour: 0, total: 0 }]
												}))
											}
										>
											+ {copy('Add', 'Hinzufügen', 'Dodaj')}
										</button>
									</div>
									{labor.length === 0 ? (
										<p className="text-muted mb-0">{copy('No labor items.', 'Keine Arbeit.', 'Nema rada.')}</p>
									) : (
										labor.map((l, idx) => (
											<div key={idx} className="mb-2 p-2 border rounded d-flex gap-1">
												<div className="flex-grow-1">
													<input
														type="text"
														className="form-control form-control-sm mb-1"
														placeholder={copy('Task', 'Aufgabe', 'Zadatak')}
														value={l.name}
														onChange={(e) =>
															updatePricing((pb) => {
																const next = [...pb.labor];
																next[idx] = { ...next[idx], name: e.target.value };
																return { ...pb, labor: next };
															})
														}
													/>
													<div className="d-flex gap-1 align-items-center">
														<input
															type="number"
															className="form-control form-control-sm"
															style={{ width: '4rem' }}
															placeholder={copy('Hrs', 'Std.', 'Sati')}
															value={l.hours || ''}
															onChange={(e) =>
																updatePricing((pb) => {
																	const next = [...pb.labor];
																	const h = Number(e.target.value) || 0;
																	next[idx] = { ...next[idx], hours: h, total: h * next[idx].ratePerHour };
																	return { ...pb, labor: next };
																})
															}
														/>
														<input
															type="number"
															className="form-control form-control-sm"
															style={{ width: '5rem' }}
															placeholder={copy('Rate', 'Satz', 'Stopa')}
															value={l.ratePerHour || ''}
															onChange={(e) =>
																updatePricing((pb) => {
																	const next = [...pb.labor];
																	const r = Number(e.target.value) || 0;
																	next[idx] = { ...next[idx], ratePerHour: r, total: next[idx].hours * r };
																	return { ...pb, labor: next };
																})
															}
														/>
														<span className="small">{Number(l.total || 0).toLocaleString()} EUR</span>
													</div>
												</div>
												<button
													type="button"
													className="btn btn-sm btn-outline-danger p-1 align-self-start"
													onClick={() =>
														updatePricing((pb) => ({
															...pb,
															labor: pb.labor.filter((_, i) => i !== idx)
														}))
													}
												>
													<i className="bi bi-trash text-danger" aria-hidden />
												</button>
											</div>
										))
									)}
								</div>
							</>
						) : (
							<PlanIndicativePricingCard
								pricing={pricing}
								copy={copy}
								showDisclaimer={false}
								embedded
							/>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
